import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, setAccessToken, clearAccessToken } from '@/api/client';
import type { User, Tenant, AuthResponse, LoginCredentials } from '@/types';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface CurrentUserResponse {
  user: User;
  tenant: Tenant;
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'currentUser'] as const,
};

// ─────────────────────────────────────────────────────────────
// Storage Helpers
// ─────────────────────────────────────────────────────────────

const REFRESH_TOKEN_KEY = 'refreshToken';

function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

function setStoredTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt: string,
  persistent: boolean = true
): void {
  const storage = persistent ? localStorage : sessionStorage;
  setAccessToken(accessToken, expiresAt);
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearStoredTokens(): void {
  clearAccessToken();
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchCurrentUser(): Promise<CurrentUserResponse> {
  return apiClient.get<CurrentUserResponse>('/auth/me');
}

async function loginUser(credentials: LoginCredentials): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/login', credentials);
}

async function logoutUser(): Promise<void> {
  await apiClient.post('/auth/logout');
}

async function refreshUserToken(refreshToken: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/refresh', { refreshToken });
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// NO `useLogout` HERE — ON PURPOSE.
//
// This module used to export one. It cleared the tokens and the query
// cache correctly, and it had ZERO callers: the UI (Sidebar) has always
// gone through `AuthContext.logout()`, which is the only place that can
// also reset the auth state. Two logout implementations meant the
// working one was never exercised, and the shipped one leaked the query
// cache across sessions for as long as that lasted (A-3).
//
// Session teardown lives in `src/contexts/AuthContext.tsx` — `logout()`
// and the shared `endSession()` it calls. Add nothing here.
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Password Update Types and API
// ─────────────────────────────────────────────────────────────

interface UpdatePasswordInput {
  currentPassword: string;
  newPassword: string;
}

interface UpdatePasswordResponse {
  success: boolean;
  message: string;
}

async function updatePassword(input: UpdatePasswordInput): Promise<UpdatePasswordResponse> {
  return apiClient.put<UpdatePasswordResponse>('/auth/password', input);
}

/**
 * Hook for updating user password
 * PUT /auth/password
 */
export function useUpdatePassword() {
  return useMutation({
    mutationFn: (input: UpdatePasswordInput) => updatePassword(input),
  });
}

// ─────────────────────────────────────────────────────────────
// Profile Update Types and API
// ─────────────────────────────────────────────────────────────

interface UserPreferences {
  language?: string;
  theme?: string;
  dateFormat?: string;
  sidebarCollapsed?: boolean;
}

interface UpdateProfileInput {
  name?: string;
  /** Numéro de téléphone au format E.164 (+226XXXXXXXX) — null pour effacer */
  phone?: string | null;
  preferences?: UserPreferences;
}

// PUT /auth/profile returns the updated user fields directly
// (apiClient already unwraps the { success, data } envelope).
type UpdateProfileResponse = Partial<User>;

async function updateProfile(input: UpdateProfileInput): Promise<UpdateProfileResponse> {
  return apiClient.put<UpdateProfileResponse>('/auth/profile', input);
}

/**
 * Hook for updating user profile
 * PUT /auth/profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProfileInput) => updateProfile(input),
    onSuccess: (data) => {
      // Merge the updated fields into the cached user. The profile endpoint
      // does not return roles/permissions, so a full replacement would drop
      // them — merge instead.
      queryClient.setQueryData(authKeys.currentUser(), (old: CurrentUserResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          user: { ...old.user, ...data },
        };
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  getStoredRefreshToken,
  setStoredTokens,
  clearStoredTokens,
  fetchCurrentUser,
  loginUser,
  logoutUser,
  refreshUserToken,
};
